import { RcFile } from 'antd/lib/upload';
import isEmpty from 'lodash/isEmpty';
import { z } from 'zod';

import { SelectOption } from '@/shared/types';

import { FileFormat, FileType } from '@/helpers/fileHelper';

import { ONE_MB } from '@/lib/consts';

export interface ValidateInputValueResult {
  error?: string;
  isValid: boolean;
}

export const isEmailRegExp =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?:[a-zA-Z]{2}|com|org|net|gov|mil|biz|info|jobs|ua|test|pro)\b$/;

export const nonUkrainianChars = /[^А-ЯІЇЄҐа-яіїєґ'’\s-]/;
const ukranianLettersUpperCaseRegExp = /[А-ЩЬЮЯЇІЄҐ]/;
const ukranianLettersLowerCaseRegExp = /[а-щьюяїієґ]/;
export const isUkrainianLetterRegExp = new RegExp(
  ukranianLettersUpperCaseRegExp.source + '|' + ukranianLettersLowerCaseRegExp.source,
);
export const isLatinLetterRegExp = /[A-Za-z]/;
export const isRussianLetterRegExp = /[ЭЪЫЁэъыё]/;
export const datePattern = (dateSeparators: string = possibleDateSeparators.join('')): string => {
  const yearPattern = '\\d{4}';
  const monthPattern = '(0[1-9]|1[0-2])';
  const dayPattern = '(0[1-9]|[12][0-9]|3[01])';
  const timePattern = '([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]';
  const dateSepPattern = `[${dateSeparators}]`;
  return `^${yearPattern}${dateSepPattern}${monthPattern}${dateSepPattern}${dayPattern}` + `(?:\\s${timePattern})?$`;
};

export const isUkrainianPassportSeriaAndNumber = (value: string): boolean =>
  new RegExp('^' + ukranianLettersUpperCaseRegExp.source + '{2}[0-9]{6}$').test(value);
export const isForeignPassportSeriaAndNumber = (value: string): boolean => /^[A-Z]{2}[0-9]{6}$/.test(value);
export const isIdCardNumber = (value: string): boolean => /^[0-9]{9}$/.test(value);
export const isLettersAndDigits = (value: string): boolean => /^[a-zA-Zа-яА-ЯІіЇїЄє0-9\s-'’',]+$/.test(value);
export const isRussianLetters = (value: string): boolean => /^[ЭЪЫЁэъыё]+$/i.test(value);

export const isOnlyDigits = (value: string): boolean => /^([0-9]?)+$/.test(value);
export const isDateTimeISO = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value);

export const isFormattedDate = (value: string, dateSeparators: string = possibleDateSeparators.join('')): boolean => {
  return new RegExp(datePattern(dateSeparators)).test(value);
};

const possibleDateSeparators = ['.', '/', '-'];
export const INVALID_EMAIL_SYMBOLS = ['"', '(', ')', ',', ':', ';', '<', '>', '[', ']', '№'];
const forbiddenEmailsList = ['yandex', 'mail.ua', 'mail.ru', 'ru', 'example', 'inbox', 'list', 'bk'];
export const UA_MOBILE_PHONE_CODES_BY_OPERATOR = {
  Kyivstar: ['039', '067', '068', '096', '097', '098', '077'],
  Vodafone: ['050', '066', '075', '095', '099'],
  Lifecell: ['063', '073', '093'],
  Utel: ['091'],
  PEOPLEnet: ['092'],
  Intertelecom: ['089', '094'],
};
export const UA_HOME_PHONE_CODES = [
  '031',
  '032',
  '033',
  '034',
  '035',
  '036',
  '037',
  '038',
  '041',
  '043',
  '044',
  '045',
  '046',
  '047',
  '048',
  '051',
  '052',
  '053',
  '054',
  '055',
  '056',
  '057',
  '061',
  '062',
  '064',
  '065',
];

export enum PhoneType {
  home = 'home',
  mobile = 'mobile',
  both = 'both',
}

export const isForbiddenEmail = (email: string): boolean => {
  const emailDomain = email.split('@')?.[1]?.toLowerCase() || '';

  return forbiddenEmailsList.some(
    (forbiddenEmail) => emailDomain === forbiddenEmail || emailDomain.split('.').includes(forbiddenEmail),
  );
};

export const validateEmail = (value = ''): ValidateInputValueResult => {
  const emailParts = value?.split('@');
  const localPart = emailParts?.[0];
  const domainPart = emailParts?.[1]?.toLowerCase();

  const isFullEmailLengthValid = value.length <= 254;
  const isLocalPartLengthValid = localPart?.length <= 64;
  const isDomainPartLengthValid = domainPart?.split('.').every((domain) => domain.length <= 64);

  const isEmail = isEmailRegExp.test([localPart, domainPart].join('@'));

  const withoutForbiddenDomains = !isForbiddenEmail(value);
  const withoutSymbols = value.split('').every((symbol) => !INVALID_EMAIL_SYMBOLS.includes(symbol));

  const isLengthValid = isFullEmailLengthValid && isLocalPartLengthValid && isDomainPartLengthValid;
  const isValid = isEmail && withoutForbiddenDomains && isLengthValid && withoutSymbols;

  let error = '';

  if (!isValid) {
    switch (false) {
      case !!value:
        error = 'Необхідно заповнити поле';
        break;
      case withoutSymbols:
      case isDomainPartLengthValid:
      case isLocalPartLengthValid:
        error = 'Недопустимий Email';
        break;

      case isFullEmailLengthValid:
        error = 'Введений Email перевищує максимальну дозволену кількість символів';
        break;

      case withoutForbiddenDomains:
        error = 'Недопустимий поштовий сервіс';
        break;

      case isEmail:
        error = 'Email має бути в форматі example@example.com';
        break;

      default:
        break;
    }
  }
  return { isValid, error };
};

export const validateField = (value: Nullable<string | SelectOption> = ''): ValidateInputValueResult => {
  const error = !isEmpty(value) ? '' : 'Необхідно заповнити поле';

  return { error, isValid: !error };
};

export const validatePhone = (
  value = '',
  phoneType = PhoneType.mobile,
  ignorePhoneCodeValidation = false,
): ValidateInputValueResult => {
  const valueWithoutPhoneMask = value.replace(/[()\s-]/g, '');

  const isPhone = /\+\d{12}/.test(valueWithoutPhoneMask);
  const isUkrPhoneCode =
    ignorePhoneCodeValidation ||
    Object.values(
      phoneType === PhoneType.mobile
        ? UA_MOBILE_PHONE_CODES_BY_OPERATOR
        : phoneType === PhoneType.home
          ? UA_HOME_PHONE_CODES
          : { ...UA_MOBILE_PHONE_CODES_BY_OPERATOR, homePhoneCodes: UA_HOME_PHONE_CODES },
    )
      .flat(1)
      .includes(valueWithoutPhoneMask.slice(3, 6));

  const isValid = isPhone && isUkrPhoneCode;
  const error = !isValid ? (!isPhone ? 'Неправильно вказаний номер телефону' : 'Недопустимий формат телефону') : '';

  return { isValid, error };
};

export const validateHouseDetails = (value = '', validLength = 5): ValidateInputValueResult => {
  if (!value) {
    return { isValid: false, error: '' };
  }
  const regex = /^[А-ЩA-ZЬЮЯЇІЄҐ)(0-9\s\-\/']+$/i;

  const isLengthValid = value.length <= validLength;
  const areCharactersValid = regex.test(value);
  const isValid = areCharactersValid && isLengthValid;

  let error = '';

  if (!isValid) {
    switch (false) {
      case isLengthValid:
        error = `Допустима кількість символів - ${validLength}`;
        break;

      case areCharactersValid:
        error = 'Тільки літери, цифри, дефіс, слеш та дужки';
        break;

      default:
        break;
    }
  }

  return { isValid, error };
};

export const validateAddress = (value = ''): ValidateInputValueResult => {
  const regex = /^[А-ЩЬЮЯЇІЄҐа-щьюяїієґ\s\-']+$/; // only UA letters, spaces, hyphens, apostrophes

  const isLettersValid = !value || regex.test(value);
  const error = (!isLettersValid && 'Тільки українською мовою') || '';

  return {
    isValid: isLettersValid,
    error,
  };
};

const ERROR_MESSAGES = {
  EMPTY: 'Необхідно заповнити поле',
  INVALID_LENGTH: 'Поштовий індекс має містити 5 символів',
  NOT_NUMERIC: 'Поштовий індекс має містити тільки цифри',
};

export const validatePostIndex = (value = ''): ValidateInputValueResult => {
  const indexLength: number = 5;
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.EMPTY,
    };
  }

  if (trimmedValue.length !== indexLength) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.INVALID_LENGTH,
    };
  }

  if (!/^\d+$/.test(trimmedValue)) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.NOT_NUMERIC,
    };
  }

  return {
    isValid: true,
    error: '',
  };
};

export const validateSettlement = (value = ''): ValidateInputValueResult => {
  const regex = /^[А-ЩЬЮЯЇІЄҐа-щьюяїієґ0-9\s\-']+$/; // only UA letters, spaces, hyphens, apostrophes, and numbers

  const isLettersValid = !value || regex.test(value);
  const error = (!isLettersValid && 'Тільки українська мова, тире та цифри') || '';

  return {
    isValid: isLettersValid,
    error,
  };
};

export const validateAddressIndex = (value: string): ValidateInputValueResult => {
  const validLength = 5;
  const areCharactersValid = isOnlyDigits(value);
  const isLengthValid = value.length === validLength;

  const isValid = areCharactersValid && isLengthValid;
  let error = '';

  if (!isValid) {
    switch (false) {
      case !!value:
        error = 'Необхідно заповнити поле';
        break;

      case isLengthValid:
        error = `Допустима кількість символів - ${validLength}`;
        break;

      case areCharactersValid:
        error = 'Лише цифри';
        break;

      default:
        break;
    }
  }

  return {
    isValid,
    error,
  };
};

export const fileHasHeicExtension = (file: File | RcFile): boolean =>
  file.type === FileType.HEIC || file.name.split('.').pop() === FileFormat.HEIC.slice(1);
export const fileHasHeifExtension = (file: File | RcFile): boolean =>
  file.type === FileType.HEIF || file.name.split('.').pop() === FileFormat.HEIF.slice(1);

export const isValidFileType = (file: File | RcFile, allowedTypes: string[]): boolean => {
  return allowedTypes.some((allowedFileType) => {
    switch (allowedFileType) {
      case FileType.ANY_IMAGE:
      case FileType.ANY_AUDIO:
      case FileType.ANY_VIDEO:
      case FileType.ANY_TEXT:
        return file.type.split('/')[0] === allowedFileType.split('/')[0];

      case FileType.HEIF:
        return fileHasHeifExtension(file);
      case FileType.HEIC:
        return fileHasHeicExtension(file);

      default:
        return file.type === allowedFileType;
    }
  });
};
export const validateFileBeforeUpload = (
  file: File | RcFile,
  maxSize: number,
  allowedTypes: FileType[] = [FileType.PDF, FileType.PNG, FileType.JPEG],
): ValidateInputValueResult => {
  const isSizeOk = file.size / (ONE_MB * ONE_MB) <= maxSize;
  const hasAllowedType = isValidFileType(file, allowedTypes);

  let error = '';

  switch (true) {
    case !hasAllowedType:
      error = 'Файл з неприпустимим розширенням';
      break;
    case !isSizeOk:
      error = 'Занадто великий файл';
      break;
    default:
      error = '';
  }

  return {
    isValid: isSizeOk && hasAllowedType,
    error,
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getPhoneSchema(phoneType: PhoneType = PhoneType.mobile, ignorePhoneCodeValidation = false) {
  return z.string().superRefine((inputValue, ctx) => {
    const valueWithoutMask = inputValue.replace(/[()\s-]/g, '');

    const isPhone = /^\+\d{12}$/.test(valueWithoutMask);
    if (!isPhone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Неправильно вказаний номер телефону',
      });
      return;
    }

    if (ignorePhoneCodeValidation) {
      return;
    }

    const codes =
      phoneType === PhoneType.mobile ? Object.values(UA_MOBILE_PHONE_CODES_BY_OPERATOR).flat() : UA_HOME_PHONE_CODES;

    const operatorCode = valueWithoutMask.slice(3, 6);

    if (!codes.includes(operatorCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Недопустимий формат телефону',
      });
    }
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const emailSchema = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => isEmailRegExp.test(email), { message: 'Недопустимий формат Email' })
  .refine((email) => !isForbiddenEmail(email), { message: 'Недопустимий поштовий сервіс' });

export const passportSerialNumberSchema = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[А-ЩЬЮЯЇІЄҐ]{2}[0-9]{6}$/.test(email), {
    message: 'Недопустимий формат',
  });

export const passportSerialNumberSchemaEn = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[A-Z]{2}[0-9]{6}$/.test(email), {
    message: 'Недопустимий формат',
  });

const serialNumberDocLength = 120;
export const docSerialNumberSchema120 = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((value) => value.length <= 120, { message: `Допустима кількість символів - ${serialNumberDocLength}` });

export const docSerialNumberOptionalSchema120 = z
  .string({ message: 'Необхідно заповнити поле' })
  .nullish()
  .optional()
  .refine((value) => !value || value.length <= serialNumberDocLength, {
    message: `Допустима кількість символів - ${serialNumberDocLength}`,
  });

export const docDateSchema = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(email), {
    message: 'Недопустимий формат',
  });
export const docDateOptionalSchema = z
  .string({ message: 'Необхідно заповнити поле' })
  .nullish()
  .optional()
  .refine((value) => !value || /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value), {
    message: 'Недопустимий формат',
  });

export const docIssueSchema4 = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[0-9]{4}$/.test(email), {
    message: 'Недопустимий формат',
  });
export const docSerialSchema9 = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[0-9]{9}$/.test(email), {
    message: 'Недопустимий формат',
  });

export const optionalDocDateSchema = z
  .string()
  .nullish()
  .optional()
  .refine((value) => !value || /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value), {
    message: 'Недопустимий формат',
  });

export const selectOptionSchema = z.object(
  {
    id: z.string(),
    label: z.string(),
  },
  { message: 'Необхідно заповнити поле' },
);

export const selectOptionalOptionSchema = z.union([
  z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .nullish(),
  z.string().nullish(),
]);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getAddressSchemaWithSlash = (max = 100, min?: number) =>
  z
    .string({ message: 'Необхідно заповнити поле' })
    .nullish()
    .optional()
    .refine((value) => !value || !min || (min && value.length >= min), {
      message: `Мінімальна кількість символів - ${min}`,
    })
    .refine((value) => !value || value.length <= max, { message: `Допустима кількість символів - ${max}` })
    .refine((value) => !value || /^[А-ЩA-ZЬЮЯЇІЄҐ)(0-9\s\-/']+$/i.test(value), {
      message: 'Тільки літери, цифри, дефіс, слеш та дужки',
    });

export const getAddressSchema = (max = 100, min?: number) =>
  z
    .string({ message: 'Необхідно заповнити поле' })
    .nullish()
    .optional()
    .refine((value) => !value || !min || (min && value.length >= min), {
      message: `Мінімальна кількість символів - ${min}`,
    })
    .refine((value) => !value || value.length <= max, { message: `Допустима кількість символів - ${max}` })
    .refine((value) => !value || /^[А-ЩЬЮЯЇІЄҐа-щьюяїієґ0-9\s\-']+$/.test(value), {
      message: 'Тільки українська мова, тире та цифри',
    });

export const unzrSchema = z
  .string({ message: 'Необхідно заповнити поле' })
  .refine((value) => value.length !== 0, { message: 'Необхідно заповнити поле' })
  .refine((email) => /^[0-9]{8}-[0-9]{5}$/.test(email), {
    message: 'Недопустимий формат',
  });
